import { EuActivityTypes, EuObjectives } from '@upright/common/model/input/eu-taxonomy/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Valence } from '@upright/common/model/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { formatRatioAsPercentage } from '@upright/ui/common/generic/utilities/format';
import { LensBoxMode } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { getEuObjectiveIcon } from '@upright/ui/common/upright/core/impact-lenses/taxonomy/common/eu-objective-icon';
import { taxonomyLensLayout } from '@upright/ui/common/upright/core/impact-lenses/taxonomy/common/layout';
import { useTaxonomyLensContext } from '@upright/ui/common/upright/core/impact-lenses/taxonomy/common/use-taxonomy-lens-context';
import styled from 'styled-components';
import { valenceToSide } from '../../../impact-lens-profile/common/utils';
import { useCarouselLayout } from '../../../impact-lens-profile/context';

interface Props {
  impact: ImpactCategoryId;
  index: number;
  objective: EuObjectives;
  valence: Valence;
}

export function TaxonomyIndicatorBox(props: Props) {
  const { impact, valence, objective } = props;
  const baseLayout = useCarouselLayout();
  const ctx = useTaxonomyLensContext();
  const kpis = ctx?.data.taxonomyKpis;

  if (!kpis) {
    return <></>;
  }
  const layout = taxonomyLensLayout(baseLayout, kpis);
  const Icon = getEuObjectiveIcon(props.objective);
  const bounds = layout.impactEuTaxonomyIndicatorBoxArea(props.index, valenceToSide(valence), impact);

  const isNewObjective = objective !== EuObjectives.mitigation && objective !== EuObjectives.adaptation;
  const datum = kpis[objective]?.[EuActivityTypes.any];
  const showPending = isNewObjective && !datum;
  const color = showPending ? BouColor.GREY_B3B3B3 : BouColor.BLACK;

  function getValue() {
    if (showPending) {
      if (bounds.width >= 64) {
        return 'Pending spec. ';
      } else if (bounds.width >= 32) {
        return 'Pending';
      } else {
        return undefined;
      }
    } else if (layout.showFiguresOnDataArea) {
      return formatRatioAsPercentage(datum?.eligibility?.value, 0);
    }
    return undefined;
  }

  const boxModeModal = baseLayout.boxMode === LensBoxMode.MODAL;
  const symbolHeight = !boxModeModal ? bounds.height - 8 : bounds.height - 2;
  const iconX = bounds.x1 + 8;
  const transform = `translate(${iconX},${bounds.y1 + (bounds.height - symbolHeight) / 2})`;
  const boxWidth = isNewObjective && !showPending ? 64 : bounds.width;
  const boxTextXAdjustment = isNewObjective && !showPending ? -6 - 64 : -6;

  return (
    <>
      {<Rect rx={1} ry={1} x={bounds.x1} y={bounds.y1} height={bounds.height} width={boxWidth} stroke={color} />}
      <g transform={transform}>
        <Icon style={{ stroke: color, fill: color }} width={symbolHeight} height={symbolHeight} />
      </g>
      {layout.showFiguresOnDataArea && (
        <Text
          stroke="transparent"
          fill={color}
          x={bounds.x2 + boxTextXAdjustment}
          y={bounds.y1 + bounds.height / 2}
          dy="0.35em"
          textAnchor="end"
        >
          {getValue() || 'N/A'}
        </Text>
      )}
    </>
  );
}

const Text = styled.text`
  font-weight: 400;
  letter-spacing: 2px;
`;

const Rect = styled.rect`
  fill: transparent;
`;
